import { Box, Checkbox, Grid, Text } from '@factorialco/deprecated-design-system'
import { RichTextDisplay, RichTextEditor } from '@factorialco/f0-react/dist/experimental'
import { FormikProps } from 'formik'
import {
  TrainingRequestDetailFragment,
  TrainingRequestsEmployeeFragment,
  TrainingsTrainingRequestScheduleEnum,
} from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import MoneyField from 'modules/core/components/MoneyField'
import Select from 'modules/core/components/Select'
import Selector from 'modules/core/components/Selector'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { ApplicantBlock } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/ApplicantBlock'
import { CompetenciesBlock } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/CompetenciesBlock'
import { ParticipantsBlock } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/ParticipantBlock'
import { useCurrentAccessLegalEntity } from 'modules/trainings/hooks/Trainings/useCurrentAccessLegalEntity'
import { RequestType, UpdateData } from 'modules/trainings/pages/MyTrainings/RequestDetail/types'

interface Props {
  trainingRequest: TrainingRequestDetailFragment
  form: FormikProps<UpdateData>
  selectedRequestType: string
  id?: string
  readonly: boolean
  employees: TrainingRequestsEmployeeFragment[]
  onChangeTrainingType: (value: RequestType) => void
}

type Schedule = {
  label: string
  value: string
}

export const NewProposalComponent = ({
  trainingRequest,
  form,
  onChangeTrainingType,
  selectedRequestType,
  readonly,
  id,
  employees,
}: Props) => {
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()

  const scheduleOptions: Schedule[] = [
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.during_work_hours'),
      value: 'workinghours',
    },
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.mixed_working_hours'),
      value: 'mixedhours',
    },
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.not_sure'),
      value: 'notsure',
    },
    {
      label: i18n.t('trainings.training_requests.create.schedule_options.outside_working_hours'),
      value: 'outsideworkinghours',
    },
  ]
  const { legalEntity } = useCurrentAccessLegalEntity()

  return (
    <Box paddingBottom="s12">
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 6 }} gridRowSpan={10}>
          <Box gap="s24">
            <Select
              label={i18n.t('trainings.training_requests.my_requests.detail.training_type.title')}
              options={[
                {
                  label: i18n.t('trainings.training_requests.create.button.new'),
                  value: 'newRequest',
                },
                {
                  label: i18n.t('trainings.training_requests.create.button.from_catalog'),
                  value: 'fromCatalog',
                },
              ]}
              readonly={readonly}
              value={selectedRequestType}
              onChange={(value) => onChangeTrainingType(value as RequestType)}
            />
            <TextField
              value={form.values.trainingName}
              label={i18n.t('trainings.training_requests.create.training_name')}
              readonly={readonly}
              onChange={(value) => form.setValues({ ...form.values, trainingName: value })}
              mandatory
            />
            <Box gap="s4">
              <Text size="200" weight="medium" color="grey1000">
                {i18n.t('trainings.training_requests.create.training_need_step')}
              </Text>
              {richTextEditorLoading ? (
                <RichTextEditor.Skeleton />
              ) : canUseRichTextEditor ? (
                readonly ? (
                  <Box
                    border={{ all: { color: 'grey400', style: 'solid', width: 's2' } }}
                    borderRadius={{ all: 'abs008' }}
                    background="grey200"
                    paddingX="s16"
                    paddingY="s8"
                    minHeight="s64"
                  >
                    <RichTextDisplay content={form.values.trainingNeed || ''} />
                  </Box>
                ) : (
                  <RichTextEditor
                    {...editorConfig}
                    title={i18n.t('trainings.training_requests.create.training_need_step')}
                    onChange={(data) => {
                      form.setValues({ ...form.values, trainingNeed: data?.value || '' })
                    }}
                    initialEditorState={{ content: form.values.trainingNeed || '' }}
                    placeholder={''}
                    maxCharacters={5000}
                  />
                )
              ) : (
                <TextArea
                  value={String(form.values.trainingNeed)}
                  readonly={readonly}
                  onChange={(value) => form.setValues({ ...form.values, trainingNeed: value })}
                  mandatory
                />
              )}
            </Box>
            <CompetenciesBlock readonly={readonly} form={form} />
            <TextField
              value={form.values.providerName}
              label={i18n.t('trainings.training_requests.create.provider_name')}
              readonly={readonly}
              onChange={(value) => form.setValues({ ...form.values, providerName: value })}
            />
            <TextField
              value={form.values.externalReference}
              label={i18n.t('trainings.training_requests.create.external_reference')}
              readonly={readonly}
              onChange={(value) => form.setValues({ ...form.values, externalReference: value })}
            />
            <Box gap="s4">
              <Text size="200" weight="medium" color="grey1000">
                {i18n.t('trainings.training_requests.create.training_hours')}
              </Text>
              <Box flexDirection="row" gap="s24">
                <TextField
                  readonly={readonly}
                  type="number"
                  value={String(form.values.duration) || ''}
                  placeholder={i18n.t(
                    'trainings.training_requests.create.duration_hours_placeholder'
                  )}
                  onChange={(value) => form.setValues({ ...form.values, duration: Number(value) })}
                />
                <TextField
                  readonly={readonly}
                  type="number"
                  value={String(form.values.minutes) || ''}
                  placeholder={i18n.t(
                    'trainings.training_requests.create.duration_minutes_placeholder'
                  )}
                  onChange={(value) => form.setValues({ ...form.values, minutes: Number(value) })}
                />
              </Box>
            </Box>
            <Box flexDirection="row" gap="s24">
              <DayPicker
                label={i18n.t('trainings.training_requests.create.dates')}
                readonly={readonly}
                value={[form.values.startDate, form.values.endDate]}
                mode="range"
                onChange={(value) =>
                  form.setValues({ ...form.values, startDate: value[0], endDate: value[1] })
                }
                placeholder={'from - to'}
              />
              <MoneyField
                readonly={readonly}
                label={i18n.t('trainings.training_requests.create.cost')}
                currency={legalEntity?.currency || ''}
                value={form.values.cost ? String(form.values.cost) : ''}
                placeholder="0"
                onChange={(value) => form.setValues({ ...form.values, cost: Number(value) })}
              />
            </Box>
            <Box flexDirection="row" gap="s8">
              <Checkbox
                checked={form.values.subsidized}
                onChange={() =>
                  form.setValues({ ...form.values, subsidized: !form.values.subsidized })
                }
                size="large"
                disabled={readonly}
              />
              <Text size="200" color="grey1000">
                {i18n.t('trainings.training_requests.my_requests.detail.subsidized')}
              </Text>
            </Box>
            <Selector.Popover
              isDisabled={readonly}
              label={i18n.t('trainings.training_requests.create.training_schedule')}
              placeholder={i18n.t('trainings.training_requests.create.select_placeholder')}
              searchPlaceholder={i18n.t('common.search')}
              blankLabel=""
              value={[form.values.schedule]}
              onChange={(selection) =>
                form.setValues({
                  ...form.values,
                  schedule: selection as TrainingsTrainingRequestScheduleEnum,
                })
              }
            >
              {scheduleOptions.map((option, index) => (
                <Selector.Option
                  key={index}
                  value={option.value}
                  label={option.label}
                  searchValue={option.label}
                >
                  {option.label}
                </Selector.Option>
              ))}
            </Selector.Popover>
          </Box>
        </Box>
        <Box gridColSpan={{ xxs: 6 }} gridRowSpan={10}>
          <Box gap="s24">
            <ApplicantBlock trainingRequest={trainingRequest} />
            {employees && employees.length > 0 && (
              <ParticipantsBlock
                trainingRequest={trainingRequest}
                readonly={readonly}
                form={form}
                employees={employees}
              />
            )}
            <Box gap="s4">
              <Text size="200" weight="medium" color="grey1000">
                {i18n.t('trainings.training_requests.my_requests.detail.additional_info')}
              </Text>
              {richTextEditorLoading ? (
                <RichTextEditor.Skeleton />
              ) : canUseRichTextEditor ? (
                readonly ? (
                  <Box
                    border={{ all: { color: 'grey400', style: 'solid', width: 's2' } }}
                    borderRadius={{ all: 'abs008' }}
                    background="grey200"
                    paddingX="s16"
                    paddingY="s8"
                    minHeight="s64"
                  >
                    <RichTextDisplay content={form.values.comments || ''} />
                  </Box>
                ) : (
                  <RichTextEditor
                    {...editorConfig}
                    title={i18n.t('trainings.training_requests.my_requests.detail.additional_info')}
                    onChange={(data) => {
                      form.setValues({ ...form.values, comments: data?.value || '' })
                    }}
                    initialEditorState={{ content: form.values.comments || '' }}
                    placeholder={''}
                    maxCharacters={5000}
                  />
                )
              ) : (
                <TextArea
                  value={String(form.values.comments)}
                  readonly={readonly}
                  onChange={(value) => form.setValues({ ...form.values, comments: value })}
                />
              )}
            </Box>
            <ApprovalsInformationBlock
              resourceId={Number(id)}
              resourceType={'Trainings::TrainingRequest'}
            />
          </Box>
        </Box>
      </Grid>
    </Box>
  )
}
